import { addTime } from '@arcadiapower/warbler';
import { isObject } from 'lodash';
import storePackage from 'store/dist/store.modern';
import expirePlugin from 'store/plugins/expire';
import { AnyState } from 'xstate';
import { SecondaryIdFieldMetadata } from '~/components/inputs/secondary-identifier/secondary-identifier.component';
import { GoogleUser } from '~/providers/query-params/context';
import { EnrollmentDataSource } from '~/typings/context';
import { FunnelTrack } from '~/typings/graphql';

type Expiration =
  | number
  | Partial<{
      days: number;
      hours: number;
      minutes: number;
      months: number;
      seconds: number;
      years: number;
    }>;

export type StoreKeyValuePair = {
  ACTIVE_FUNNEL_ID: string | undefined;
  ACTIVE_FUNNEL_ROUTE: string | undefined;
  DEV_TOOLS: boolean | undefined;
  NO_DASHBOARD: boolean | undefined;
  SHOW_DIAGRAMS: boolean | undefined;
  TOKEN: string | undefined;
  USER_ID: number | undefined;
  ACCOUNT_ID: number | undefined;
  CLIENT_IP: string | undefined;
  ZIP_CODE: string | undefined;
  UTILITY_SELECTION: Utility | undefined;
  GOOGLE_USER: GoogleUser | undefined;
  RESUMING_USER_UTILITY_ACCOUNT_ID: number | undefined;
  RESUMING_IN_PROGRESS: boolean | undefined;
  ENROLLMENT_DATA_SOURCE_NAME: EnrollmentDataSource | undefined;
  IS_IN_LMI_ZONE: boolean | undefined;
  PROOF_DOC_SKIPPED: boolean | undefined;
  SECONDARY_ID_FIELD_METADATA: SecondaryIdFieldMetadata[] | undefined;
  DOCUSIGN_USER_SIGNED_STATUS: DocusignSignedStatus;
  META_DATA: MetaData | undefined;
};

export type DocusignSignedStatus =
  | 'signing_complete'
  | 'cancel'
  | 'decline'
  | 'viewing_complete'
  | undefined;

export type MetaData = {
  carrier_name: string;
  error_code?: null | number | string;
  mobile_country_code: string;
  mobile_network_code: string;
  type: string;
};
export interface Utility {
  displayName: string;
  funnelTrack: FunnelTrack;
  id: number;
  state: {
    abbreviation: string;
    id: number;
    name: string;
  };
}
export type StoreKey = keyof StoreKeyValuePair;

interface Store {
  _originalSet(key: string, value: any, expiration?: number): void;
  clearAll(): void;
  get<TStoreKey extends StoreKey>(
    key: TStoreKey,
    optionalDefaultValue?: any,
  ): StoreKeyValuePair[TStoreKey];
  namespace(namespace: string): Store;
  set<TStoreKey extends StoreKey>(
    key: TStoreKey,
    value: StoreKeyValuePair[TStoreKey],
    expiration?: Expiration,
  ): void;
  remove(key: StoreKey): void;
  getFunnel(key: string): AnyState | undefined;
  setFunnel(key: string, value: AnyState): void;
}

storePackage.addPlugin(expirePlugin);
export const store = storePackage.namespace('funnel') as Store;

store._originalSet = store.set;
store.set = (key, value, _expiration) => {
  const expiration = isObject(_expiration)
    ? (addTime(new Date(), _expiration) as Date).getTime()
    : _expiration;

  store._originalSet(key, value, expiration);
};
store.setFunnel = store.set as Store['setFunnel'];
store.getFunnel = store.get as Store['getFunnel'];
